/**
 * チャット入力エリアコンポーネント
 *
 * 画像添付とテキスト入力を含む入力エリア
 */

import {
  useRef,
  KeyboardEvent,
  ClipboardEvent,
  ChangeEvent,
} from "react";
import { Box, Group, TextInput, ActionIcon, Tooltip } from "@mantine/core";
import { Send, ImagePlus } from "lucide-react";
import { AttachedImage } from "../../services/assistantService";
import { ImagePreview } from "./ImagePreview";

// 最大添付画像数
const MAX_IMAGES = 3;

interface ChatInputAreaProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  attachedImages: AttachedImage[];
  onAddImages: (files: File[]) => void;
  onRemoveImage: (index: number) => void;
  onSend: () => void;
  isLoading: boolean;
}

/**
 * チャット入力エリア
 */
export function ChatInputArea({
  inputValue,
  onInputChange,
  attachedImages,
  onAddImages,
  onRemoveImage,
  onSend,
  isLoading,
}: ChatInputAreaProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * ファイル選択ダイアログから画像を追加
   */
  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const imageFiles = Array.from(files).filter((f) =>
      f.type.startsWith("image/")
    );
    onAddImages(imageFiles);

    // inputをリセット（同じファイルを再選択可能にする）
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * クリップボードから画像を貼り付け
   */
  const handlePaste = async (e: ClipboardEvent<HTMLInputElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    for (const item of Array.from(items)) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault(); // テキスト貼り付けを防ぐ
      onAddImages(imageFiles);
    }
  };

  /**
   * Enterキーで送信
   * IMEで日本語変換中（コンポジション中）の場合は送信しない
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // IMEで変換中の場合は送信しない（Mac/Windowsの日本語入力対応）
    // nativeEvent.isComposing: 標準的なコンポジション状態チェック
    // keyCode === 229: 一部のブラウザでのフォールバック
    if (e.nativeEvent.isComposing || e.keyCode === 229) {
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <Box
      p="md"
      style={{
        borderTop: "1px solid var(--mantine-color-default-border)",
      }}
    >
      {/* 画像プレビュー */}
      <ImagePreview images={attachedImages} onRemove={onRemoveImage} />

      <Group gap="xs">
        {/* 隠しファイル入力 */}
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
        <Tooltip
          label={
            attachedImages.length >= MAX_IMAGES
              ? `最大${MAX_IMAGES}枚まで`
              : "画像を添付"
          }
        >
          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={() => fileInputRef.current?.click()}
            disabled={attachedImages.length >= MAX_IMAGES}
          >
            <ImagePlus size={20} />
          </ActionIcon>
        </Tooltip>
        <TextInput
          placeholder={
            attachedImages.length > 0
              ? "画像についてコメント..."
              : "メッセージを入力... (Ctrl+Vで画像貼付)"
          }
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={isLoading}
          style={{ flex: 1 }}
          radius="xl"
        />
        <ActionIcon
          variant="filled"
          color="violet"
          size="lg"
          radius="xl"
          onClick={onSend}
          disabled={!inputValue.trim() || isLoading}
        >
          <Send size={18} />
        </ActionIcon>
      </Group>
    </Box>
  );
}
